import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Building2, FileText, LayoutDashboard, LogOut, User, Wallet } from 'lucide-react';
import { setPortalSession, usePortalSession } from './portalSession';

/**
 * Public-facing customer portal shell.
 *
 * Sticky topbar with branding · simple horizontal nav · routed outlet
 * for the dashboard / invoices / payments pages.
 *
 * Route guard: if there's no portal session, redirect to /portal/login.
 * The staff Redux auth slice is intentionally NOT consulted here — a
 * staff user signing in to the panel doesn't get auto-promoted into
 * the customer portal and vice versa.
 */
export default function PortalLayout() {
  const session = usePortalSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (!session) navigate('/portal/login', { replace: true });
  }, [session, navigate]);

  if (!session) return null;

  function signOut() {
    setPortalSession(null);
    navigate('/portal/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg-card)]/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/portal" className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <span className="grid h-7 w-7 place-items-center rounded-md bg-[var(--color-primary)] text-white">
              <Building2 size={14} />
            </span>
            <span>KSS BillBook · Portal</span>
            <span className="hidden text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] sm:inline">
              · {session.tenantCode}
            </span>
          </Link>

          <nav className="hidden items-center gap-4 text-sm sm:flex">
            <PortalLink to="/portal" end icon={<LayoutDashboard size={13} />}>Overview</PortalLink>
            <PortalLink to="/portal/invoices" icon={<FileText size={13} />}>Invoices</PortalLink>
            <PortalLink to="/portal/payments" icon={<Wallet size={13} />}>Payments</PortalLink>
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1 text-[11px] sm:inline-flex">
              <User size={11} />
              <span className="max-w-[120px] truncate">{session.customerName}</span>
            </span>
            <button
              type="button"
              onClick={signOut}
              title="Sign out"
              aria-label="Sign out"
              className="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-border)] hover:border-[var(--color-primary)]"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
        {/* Mobile nav strip */}
        <nav className="flex gap-3 overflow-x-auto border-t border-[var(--color-border)]/60 px-4 py-2 text-xs sm:hidden">
          <PortalLink to="/portal" end>Overview</PortalLink>
          <PortalLink to="/portal/invoices">Invoices</PortalLink>
          <PortalLink to="/portal/payments">Payments</PortalLink>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>

      <footer className="mt-12 border-t border-[var(--color-border)] py-4 text-center text-[11px] text-[var(--color-text-muted)]">
        © {new Date().getFullYear()} KSS BillBook · Customer support: support@kristysoftware.com
      </footer>
    </div>
  );
}

function PortalLink({
  to, end, icon, children,
}: {
  to: string;
  end?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 transition-colors ${
          isActive
            ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
